import {useEffect, useRef, useState} from "react";

// Controlled value with a debounced commit. `value` is the external source of
// truth (editor state); `commit` applies an edit (typically executeCommand).
// Returns the value to render and a setter: the setter updates the local mirror
// immediately (so a slider/color drag stays responsive) but only calls `commit`
// once the value has been still for `ms` — collapsing a burst of drag events
// into a single command (one undo entry, one Live Preview push).
//
// External changes to `value` (undo/redo, branch switch, default seeding) sync
// into the mirror, but only while no edit is pending, so they can't clobber an
// in-progress drag. A pending edit is flushed on unmount so it isn't lost if the
// input is removed (e.g. its section is collapsed) before the timer fires.
export function useDebouncedCommit<T>(
    value: T,
    commit: (v: T) => void,
    ms: number,
): [T, (v: T) => void] {
    const [local, setLocal] = useState(value);
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const pending = useRef<{ v: T } | null>(null);
    // Always call the latest commit, not the one captured when the timer armed.
    const commitRef = useRef(commit);
    commitRef.current = commit;

    useEffect(() => {
        if (timer.current === undefined) setLocal(value);
    }, [value]);

    useEffect(() => () => {
        if (timer.current !== undefined) {
            clearTimeout(timer.current);
            if (pending.current) commitRef.current(pending.current.v);
        }
    }, []);

    function set(v: T) {
        setLocal(v);
        pending.current = {v};
        if (timer.current !== undefined) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            timer.current = undefined;
            pending.current = null;
            commitRef.current(v);
        }, ms);
    }

    return [local, set];
}
